import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  BarChart3, Settings, MessageSquare, Save,
  ToggleLeft, ToggleRight, AlertTriangle, CheckCircle, Target,
  Play, ChevronDown, ChevronUp, Send, Eye, X,
  Users, FileText, ArrowRight, ArrowLeft
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = "'Tajawal', 'Segoe UI', sans-serif";

const C = {
  primary: '#1D8F4E', secondary: '#1B3A6B', gold: '#C5A572',
  bg: '#F8FAFC', bgGray: '#F1F5F9', border: '#E2E8F0',
  text: '#1E293B', muted: '#64748B', light: '#94A3B8',
  red: '#DC2626', orange: '#D97706', green: '#059669',
  greenBg: '#ECFDF5', orangeBg: '#FFFBEB',
};

const CAT_COLORS = { red: C.red, orange: C.orange, green: C.green };

function Section({ title, icon: Icon, children, open: defOpen = true }) {
  const [open, setOpen] = useState(defOpen);
  return (
    <div style={{ backgroundColor: 'white', borderRadius: 14, border: `1px solid ${C.border}`, marginBottom: 16, overflow: 'hidden' }}>
      <button onClick={() => setOpen(!open)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', backgroundColor: open ? C.bg : 'white', border: 'none', cursor: 'pointer', fontFamily: FONT }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon style={{ width: 18, height: 18, color: C.secondary }} />
          <span style={{ fontSize: 15, fontWeight: 'bold', color: C.secondary }}>{title}</span>
        </div>
        {open ? <ChevronUp style={{ width: 16, height: 16, color: C.muted }} /> : <ChevronDown style={{ width: 16, height: 16, color: C.muted }} />}
      </button>
      {open && <div style={{ padding: 20, borderTop: `1px solid ${C.border}` }}>{children}</div>}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", multi = false }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: C.text, display: 'block', marginBottom: 6, fontFamily: FONT }}>{label}</label>
      {multi
        ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 14, fontFamily: FONT, minHeight: 80, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
        : <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 14, fontFamily: FONT, outline: 'none', boxSizing: 'border-box' }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANALYSIS DETAIL MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function AnalysisDetail({ sessionId, onClose }) {
  const [data, setData] = useState(null);
  const token = localStorage.getItem("hala_admin_token");

  useEffect(() => {
    axios.get(`${API}/financial/admin/analyses/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {});
  }, [sessionId, token]);

  if (!data) return null;
  const sr = data.score_result;
  const catColor = sr ? CAT_COLORS[sr.category] || C.muted : C.muted;

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 20, padding: 24, width: '90%', maxWidth: 600, maxHeight: '85vh', overflowY: 'auto', fontFamily: FONT }} dir="rtl">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 'bold', color: C.secondary, margin: 0 }}>تفاصيل التحليل</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X style={{ width: 20, height: 20, color: C.muted }} /></button>
        </div>

        {/* Client info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'الاسم', value: data.client_name || '-' },
            { label: 'الجوال', value: data.client_mobile || '-' },
            { label: 'المنطقة', value: data.client_region || '-' },
            { label: 'الجنس', value: data.client_gender === 'male' ? 'ذكر' : data.client_gender === 'female' ? 'أنثى' : '-' },
          ].map(i => (
            <div key={i.label} style={{ backgroundColor: C.bgGray, borderRadius: 10, padding: 10 }}>
              <span style={{ fontSize: 11, color: C.muted, display: 'block' }}>{i.label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{i.value}</span>
            </div>
          ))}
        </div>

        {/* Score */}
        {sr && (
          <div style={{ textAlign: 'center', padding: 16, backgroundColor: C.bgGray, borderRadius: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 48, fontWeight: 'bold', color: catColor }}>{sr.total_score}</div>
            <div style={{ fontSize: 14, color: C.muted }}>{sr.category_label}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 12 }}>
              {Object.entries(sr.components || {}).map(([k, v]) => {
                const labels = { behavior: 'سلوك', savings: 'ادخار', expenses: 'مصروفات', debt: 'ديون' };
                const max = sr.components_max?.[k] || 25;
                return max > 0 ? (
                  <div key={k} style={{ backgroundColor: 'white', borderRadius: 8, padding: 8, textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: C.secondary }}>{v}/{max}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{labels[k]}</div>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}

        {/* Conversation log */}
        {data.conversation_log?.length > 0 && (
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 'bold', color: C.secondary, marginBottom: 10 }}>سجل المحادثة</h3>
            <div style={{ maxHeight: 300, overflowY: 'auto', backgroundColor: C.bgGray, borderRadius: 12, padding: 12 }}>
              {data.conversation_log.map((entry, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={{ backgroundColor: C.primary, color: 'white', padding: '6px 12px', borderRadius: 10, borderTopRightRadius: 2, fontSize: 12, marginBottom: 4 }}>{entry.question_text}</div>
                  <div style={{ backgroundColor: 'white', padding: '6px 12px', borderRadius: 10, borderTopLeftRadius: 2, fontSize: 12, color: C.text, textAlign: 'left', direction: 'ltr' }}>{entry.answer}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', gap: 8, fontSize: 11, color: C.muted }}>
          <span>بدأ: {data.started_at ? new Date(data.started_at).toLocaleString('ar-SA') : '-'}</span>
          <span>|</span>
          <span>اكتمل: {data.completed_at ? new Date(data.completed_at).toLocaleString('ar-SA') : '-'}</span>
          {data.converted_to_main_hala && <span style={{ color: C.green }}>| تحويل لهلا</span>}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function FinancialAdvisorSettingsPage() {
  const [tab, setTab] = useState("settings"); // settings | log
  const [config, setConfig] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Analysis log
  const [analyses, setAnalyses] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [logPages, setLogPages] = useState(1);
  const [logFilter, setLogFilter] = useState("");
  const [detailId, setDetailId] = useState(null);
  // Simulator
  const [simSessionId, setSimSessionId] = useState(null);
  const [simQuestion, setSimQuestion] = useState(null);
  const [simMessages, setSimMessages] = useState([]);
  const [simResult, setSimResult] = useState(null);
  const [simInput, setSimInput] = useState("");
  const [simLoading, setSimLoading] = useState(false);

  const token = localStorage.getItem("hala_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const loadConfig = useCallback(async () => {
    try {
      const [cr, sr] = await Promise.all([
        axios.get(`${API}/financial/admin/config`, { headers }),
        axios.get(`${API}/financial/admin/stats`, { headers }),
      ]);
      setConfig(cr.data.config); setQuestions(cr.data.questions); setStats(sr.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadAnalyses = useCallback(async (page = 1, status = "") => {
    try {
      const params = { page, limit: 15 };
      if (status) params.status = status;
      const r = await axios.get(`${API}/financial/admin/analyses`, { headers, params });
      setAnalyses(r.data.items); setLogTotal(r.data.total); setLogPages(r.data.pages); setLogPage(page);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { if (tab === "log") loadAnalyses(1, logFilter); }, [tab, logFilter, loadAnalyses]);

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await axios.put(`${API}/financial/admin/config`, config, { headers });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const u = (k, v) => setConfig(p => ({ ...p, [k]: v }));
  const uThresh = (k, v) => setConfig(p => ({ ...p, thresholds: { ...p.thresholds, [k]: Number(v) } }));
  const uSav = (k, v) => setConfig(p => ({ ...p, savings_recommendation: { ...p.savings_recommendation, [k]: Number(v) } }));
  const uAi = (k, v) => setConfig(p => ({ ...p, ai_messages: { ...p.ai_messages, [k]: v } }));
  const uVal = (k, v) => setConfig(p => ({ ...p, validation_rules: { ...p.validation_rules, [k]: v } }));

  // Simulator
  const startSim = async () => {
    setSimMessages([]); setSimResult(null); setSimLoading(true);
    try {
      const r = await axios.post(`${API}/financial/admin/simulate`, {}, { headers });
      setSimSessionId(r.data.session_id); setSimQuestion(r.data.question);
      setSimMessages([{ s: 'ai', t: r.data.question.text }]);
    } catch (e) { console.error(e); }
    setSimLoading(false);
  };

  const simAns = async (v) => {
    if (!simSessionId) return;
    const disp = simQuestion?.options?.find(o => o.value === v)?.label || `${v}`;
    setSimMessages(p => [...p, { s: 'user', t: disp }]);
    setSimQuestion(null); setSimInput(""); setSimLoading(true);
    try {
      const r = await axios.post(`${API}/financial/sessions/${simSessionId}/answer`, { answer: v });
      if (r.data.validation_error) {
        setSimMessages(p => [...p, { s: 'ai', t: `خطأ: ${r.data.validation_error}` }]);
        setSimQuestion(r.data.question);
      } else if (r.data.validation_block) {
        const block = r.data.validation_block;
        setSimMessages(p => [...p, { s: 'ai', t: `حظر التحقق: ${block.message}` }]);
        if (r.data.needs_confirmation && block.allow_confirmation) {
          // Auto-confirm Rule 10 in simulator
          const r2 = await axios.post(`${API}/financial/sessions/${simSessionId}/answer`, { answer: '', confirmed: true });
          if (r2.data.completed) {
            setSimResult(r2.data.score_result);
            setSimMessages(p => [...p, { s: 'ai', t: `النتيجة: ${r2.data.score_result.total_score}/100` }]);
          } else if (r2.data.question) {
            setSimQuestion(r2.data.question);
            setSimMessages(p => [...p, { s: 'ai', t: r2.data.question?.text || '' }]);
          }
        } else {
          // Hard block - show the question to fix
          if (r.data.question) {
            setSimQuestion(r.data.question);
            setSimMessages(p => [...p, { s: 'ai', t: `أعد إدخال: ${r.data.question.text}` }]);
          }
        }
      } else if (r.data.completed) {
        setSimResult(r.data.score_result);
        setSimMessages(p => [...p, { s: 'ai', t: `النتيجة: ${r.data.score_result.total_score}/100 (${r.data.score_result.category_label})` }]);
      } else {
        setSimQuestion(r.data.question);
        setSimMessages(p => [...p, { s: 'ai', t: r.data.question.text }]);
      }
    } catch (e) { console.error(e); }
    setSimLoading(false);
  };

  if (!config) return <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontFamily: FONT }}>جاري التحميل...</div>;

  return (
    <div style={{ fontFamily: FONT, direction: 'rtl', maxWidth: 900, margin: '0 auto' }}>
      {detailId && <AnalysisDetail sessionId={detailId} onClose={() => setDetailId(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: C.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BarChart3 style={{ width: 22, height: 22, color: C.gold }} />
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 'bold', color: C.secondary, margin: 0 }}>المستشار المالي</h1>
            <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>إعدادات وسجل التحليلات</p>
          </div>
        </div>
        {tab === "settings" && (
          <button onClick={handleSave} disabled={saving} data-testid="save-config-btn"
            style={{ padding: '10px 24px', backgroundColor: saved ? C.green : C.primary, color: 'white', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 'bold', cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 8 }}>
            {saved ? <CheckCircle style={{ width: 16, height: 16 }} /> : <Save style={{ width: 16, height: 16 }} />}
            {saved ? 'تم الحفظ' : saving ? 'جاري...' : 'حفظ'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { id: 'settings', label: 'الإعدادات', icon: Settings },
          { id: 'log', label: 'سجل التحليلات', icon: FileText },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            style={{ padding: '10px 20px', borderRadius: 10, border: `1px solid ${tab === t.id ? C.primary : C.border}`,
              backgroundColor: tab === t.id ? `${C.primary}10` : 'white', color: tab === t.id ? C.primary : C.muted,
              fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6 }}>
            <t.icon style={{ width: 14, height: 14 }} />{t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'إجمالي', value: stats.total_sessions, color: '#3B82F6' },
            { label: 'مكتملة', value: stats.completed_sessions, color: C.green },
            { label: 'نسبة الإكمال', value: `${stats.completion_rate}%`, color: C.orange },
            { label: 'متوسط النتيجة', value: stats.average_score, color: '#8B5CF6' },
            { label: 'تحويل لهلا', value: stats.converted_sessions || 0, color: C.secondary },
          ].map(s => (
            <div key={s.label} style={{ backgroundColor: 'white', borderRadius: 12, padding: 14, border: `1px solid ${C.border}`, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 'bold', color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ═══════════ SETTINGS TAB ═══════════ */}
      {tab === "settings" && (
        <>
          <Section title="الإعدادات العامة" icon={Settings}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <button onClick={() => u('enabled', !config.enabled)} data-testid="toggle-enabled" style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
                {config.enabled ? <ToggleRight style={{ width: 32, height: 32, color: C.primary }} /> : <ToggleLeft style={{ width: 32, height: 32, color: C.light }} />}
              </button>
              <span style={{ fontSize: 14, fontWeight: 600, color: config.enabled ? C.primary : C.light }}>{config.enabled ? 'مفعّل' : 'معطّل'}</span>
            </div>
            <Field label="العنوان" value={config.title || ''} onChange={v => u('title', v)} />
            <Field label="الوصف" value={config.description || ''} onChange={v => u('description', v)} multi />
            <Field label="زر البدء (CTA)" value={config.cta_text || ''} onChange={v => u('cta_text', v)} />
          </Section>

          <Section title="الأسئلة" icon={MessageSquare} open={false}>
            {questions.map((q, i) => (
              <div key={q.id} style={{ backgroundColor: C.bg, borderRadius: 10, padding: 14, marginBottom: 10, border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.secondary }}>السؤال {i + 1}: {q.id}</span>
                  <span style={{ fontSize: 11, color: C.muted, backgroundColor: C.bgGray, padding: '2px 8px', borderRadius: 9999 }}>{q.step} | {q.type}</span>
                </div>
                <p style={{ fontSize: 13, color: C.text, margin: '4px 0' }}>{q.text}</p>
                {q.options && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {q.options.map(opt => (
                      <span key={opt.value} style={{ fontSize: 11, backgroundColor: 'white', padding: '4px 10px', borderRadius: 8, border: `1px solid ${C.border}`, color: C.muted }}>
                        {opt.label} {opt.score !== undefined && `(${opt.score})`}
                      </span>
                    ))}
                  </div>
                )}
                {q.condition && <p style={{ fontSize: 11, color: C.orange, margin: '6px 0 0', fontStyle: 'italic' }}>شرط: {q.condition.field} = {q.condition.value}</p>}
              </div>
            ))}
          </Section>

          <Section title="حدود التقييم والتوصيات" icon={Target} open={false}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="الحد الأدنى للأخضر" value={config.thresholds?.green_min || 70} onChange={v => uThresh('green_min', v)} type="number" />
              <Field label="الحد الأدنى للبرتقالي" value={config.thresholds?.orange_min || 40} onChange={v => uThresh('orange_min', v)} type="number" />
            </div>
            <h4 style={{ fontSize: 13, fontWeight: 'bold', color: C.secondary, marginTop: 12, marginBottom: 8 }}>نسب الادخار المقترحة</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="الحد الأدنى (مع ديون) %" value={config.savings_recommendation?.min_with_debt || 5} onChange={v => uSav('min_with_debt', v)} type="number" />
              <Field label="الحد الأقصى (مع ديون) %" value={config.savings_recommendation?.max_with_debt || 10} onChange={v => uSav('max_with_debt', v)} type="number" />
              <Field label="الحد الأدنى (بدون ديون) %" value={config.savings_recommendation?.min_without_debt || 10} onChange={v => uSav('min_without_debt', v)} type="number" />
              <Field label="الحد الأقصى (بدون ديون) %" value={config.savings_recommendation?.max_without_debt || 20} onChange={v => uSav('max_without_debt', v)} type="number" />
            </div>
          </Section>

          <Section title="رسائل الذكاء الاصطناعي" icon={BarChart3} open={false}>
            <Field label="رسالة الوضع السيئ (أحمر)" value={config.ai_messages?.red || ''} onChange={v => uAi('red', v)} multi />
            <Field label="رسالة الوضع المتوسط (برتقالي)" value={config.ai_messages?.orange || ''} onChange={v => uAi('orange', v)} multi />
            <Field label="رسالة الوضع الجيد (أخضر)" value={config.ai_messages?.green || ''} onChange={v => uAi('green', v)} multi />
          </Section>

          <Section title="قواعد التحقق من البيانات" icon={AlertTriangle} open={false}>
            {[
              { key: 'block_if_expenses_exceed_income', label: 'حظر إذا المصروفات أعلى من الدخل' },
              { key: 'block_if_total_commitments_exceed_income', label: 'حظر إذا المصروفات والالتزامات أعلى من الدخل' },
              { key: 'block_if_saving_exceeds_income', label: 'حظر إذا الادخار أعلى من الدخل' },
              { key: 'block_if_negative_values', label: 'حظر القيم السالبة' },
              { key: 'suspicious_values_confirmation', label: 'طلب تأكيد القيم المشبوهة' },
            ].map(rule => (
              <div key={rule.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.bgGray}` }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: C.text, fontFamily: FONT }}>{rule.label}</span>
                <button onClick={() => uVal(rule.key, !config.validation_rules?.[rule.key])}
                  data-testid={`toggle-${rule.key}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
                  {config.validation_rules?.[rule.key] !== false
                    ? <ToggleRight style={{ width: 28, height: 28, color: C.primary }} />
                    : <ToggleLeft style={{ width: 28, height: 28, color: C.light }} />}
                </button>
              </div>
            ))}
            <p style={{ fontSize: 11, color: C.muted, marginTop: 12, lineHeight: 1.6 }}>
              هذه القواعد تمنع المستخدم من إكمال التحليل إذا كانت البيانات المالية متناقضة. جميع القواعد مفعّلة افتراضياً.
            </p>
          </Section>

          <Section title="نص إخلاء المسؤولية" icon={AlertTriangle} open={false}>
            <Field label="النص" value={config.disclaimer || ''} onChange={v => u('disclaimer', v)} multi />
          </Section>

          <Section title="محاكي التحليل" icon={Play} open={false}>
            <button onClick={startSim} data-testid="start-simulator-btn"
              style={{ padding: '10px 20px', backgroundColor: C.secondary, color: 'white', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 'bold', cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <Play style={{ width: 14, height: 14 }} />بدء المحاكاة
            </button>
            {simMessages.length > 0 && (
              <div style={{ backgroundColor: C.bg, borderRadius: 12, padding: 16, maxHeight: 400, overflowY: 'auto', border: `1px solid ${C.border}` }}>
                {simMessages.map((m, i) => (
                  <div key={i} style={{ marginBottom: 10, display: 'flex', justifyContent: m.s === 'user' ? 'flex-start' : 'flex-end' }}>
                    <div style={{ maxWidth: '80%', padding: '8px 14px', borderRadius: 12, fontSize: 13, lineHeight: 1.5, backgroundColor: m.s === 'ai' ? C.primary : C.bgGray, color: m.s === 'ai' ? 'white' : C.text }}>{m.t}</div>
                  </div>
                ))}
                {simLoading && <p style={{ fontSize: 12, color: C.muted, textAlign: 'center' }}>...</p>}
              </div>
            )}
            {simQuestion && !simLoading && (
              <div style={{ marginTop: 12 }}>
                {(simQuestion.type === 'choice' || simQuestion.type === 'scored_choice' || simQuestion.type === 'region_choice') && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {simQuestion.options.map(opt => (
                      <button key={opt.value} onClick={() => simAns(opt.value)}
                        style={{ padding: '8px 16px', backgroundColor: 'white', border: `1px solid ${C.border}`, borderRadius: 9999, fontSize: 13, color: C.secondary, cursor: 'pointer', fontFamily: FONT }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
                {(simQuestion.type === 'number' || simQuestion.type === 'text' || simQuestion.type === 'phone') && (
                  <form onSubmit={e => { e.preventDefault(); if (simInput.trim()) simAns(simInput.trim()); }} style={{ display: 'flex', gap: 8 }}>
                    <input type={simQuestion.type === 'number' ? 'number' : 'text'} value={simInput} onChange={e => setSimInput(e.target.value)} placeholder={simQuestion.placeholder}
                      style={{ flex: 1, padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 13, fontFamily: FONT, outline: 'none' }} />
                    <button type="submit" style={{ padding: '8px 16px', backgroundColor: C.primary, color: 'white', borderRadius: 10, border: 'none', fontSize: 13, cursor: 'pointer' }}>
                      <Send style={{ width: 14, height: 14 }} />
                    </button>
                  </form>
                )}
              </div>
            )}
            {simResult && (
              <div style={{ marginTop: 12, backgroundColor: 'white', borderRadius: 12, padding: 16, border: `1px solid ${C.border}` }}>
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 36, fontWeight: 'bold', color: CAT_COLORS[simResult.category] || C.muted }}>{simResult.total_score}</div>
                  <div style={{ fontSize: 14, color: C.muted }}>{simResult.category_label}</div>
                </div>
                {simResult.recommendation && <p style={{ fontSize: 12, color: C.text, textAlign: 'center', margin: '8px 0 0' }}>{simResult.recommendation.message}</p>}
              </div>
            )}
          </Section>
        </>
      )}

      {/* ═══════════ LOG TAB ═══════════ */}
      {tab === "log" && (
        <div>
          {/* Filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[
              { v: '', label: 'الكل' },
              { v: 'completed', label: 'مكتملة' },
              { v: 'in_progress', label: 'جارية' },
            ].map(f => (
              <button key={f.v} onClick={() => setLogFilter(f.v)}
                style={{ padding: '6px 14px', borderRadius: 9999, border: `1px solid ${logFilter === f.v ? C.primary : C.border}`,
                  backgroundColor: logFilter === f.v ? `${C.primary}10` : 'white', color: logFilter === f.v ? C.primary : C.muted,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                {f.label}
              </button>
            ))}
            <span style={{ marginRight: 'auto', fontSize: 12, color: C.muted, alignSelf: 'center' }}>{logTotal} تحليل</span>
          </div>

          {/* Table */}
          <div style={{ backgroundColor: 'white', borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: C.bg }}>
                  {['الاسم', 'الجوال', 'المنطقة', 'النتيجة', 'الحالة', 'التاريخ', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: C.secondary, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analyses.map(a => {
                  const catColor = a.category ? CAT_COLORS[a.category] : C.muted;
                  return (
                    <tr key={a.session_id} style={{ borderBottom: `1px solid ${C.bgGray}` }}>
                      <td style={{ padding: '10px 14px', fontWeight: 500 }}>{a.client_name || '-'}</td>
                      <td style={{ padding: '10px 14px', color: C.muted, direction: 'ltr', textAlign: 'right' }}>{a.client_mobile || '-'}</td>
                      <td style={{ padding: '10px 14px', color: C.muted }}>{a.client_region || '-'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {a.score != null ? (
                          <span style={{ fontWeight: 'bold', color: catColor }}>{a.score} <span style={{ fontSize: 10, fontWeight: 400 }}>{a.category_label}</span></span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 9999, backgroundColor: a.status === 'completed' ? C.greenBg : '#FEF9C3', color: a.status === 'completed' ? C.green : C.orange, border: `1px solid ${a.status === 'completed' ? '#A7F3D0' : '#FDE68A'}` }}>
                          {a.status === 'completed' ? 'مكتمل' : 'جارٍ'}
                        </span>
                        {a.converted_to_main_hala && <span style={{ fontSize: 10, color: C.secondary, marginRight: 4 }}>تحويل</span>}
                      </td>
                      <td style={{ padding: '10px 14px', color: C.muted, fontSize: 11 }}>{a.started_at ? new Date(a.started_at).toLocaleDateString('ar-SA') : '-'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <button onClick={() => setDetailId(a.session_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.primary }}>
                          <Eye style={{ width: 16, height: 16 }} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {analyses.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: C.muted }}>لا توجد تحليلات</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {logPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button disabled={logPage <= 1} onClick={() => loadAnalyses(logPage - 1, logFilter)}
                style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, backgroundColor: 'white', cursor: logPage > 1 ? 'pointer' : 'default', color: C.muted }}>
                <ArrowRight style={{ width: 14, height: 14 }} />
              </button>
              <span style={{ fontSize: 13, color: C.muted }}>{logPage} / {logPages}</span>
              <button disabled={logPage >= logPages} onClick={() => loadAnalyses(logPage + 1, logFilter)}
                style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, backgroundColor: 'white', cursor: logPage < logPages ? 'pointer' : 'default', color: C.muted }}>
                <ArrowLeft style={{ width: 14, height: 14 }} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
